import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import * as adminApi from '../api/adminApi';
import { ORDER_STATUSES, formatDate, formatEmailField, formatMoney, formatPlacedDate, formatPlacedTime } from '../lib/format';
import { humanOrderStatus } from '../lib/orders';
import { normalizeListResponse, pickErrorMessage } from '../lib/apiHelpers';
import { PageHeader, Card, CardBody, ErrorBanner, BackLink } from '../components/PageChrome';
import { DataTable } from '../components/DataTable';
import { Pagination } from '../components/Pagination';
import { FilterSubmitButton } from '../components/FilterSubmitButton';
import { Modal } from '../components/Modal';

const ORDERS_HISTORY_LIMIT = 500;
const REVIEWS_PAGE_SIZE = 5;
const FEEDBACK_PAGE_SIZE = 5;

function pickUser(payload) {
  if (!payload || typeof payload !== 'object') return null;
  return payload.user ?? payload.appUser ?? payload;
}

function orderStatusBadge(status) {
  const map = {
    CONFIRMED: 'info',
    PREPARING: 'warning',
    READY_FOR_PICKUP: 'primary',
    ON_THE_WAY: 'primary',
    DELIVERED: 'success',
    REFUNDED: 'warning',
    CANCELLED: 'danger',
  };
  const c = map[status] || 'secondary';
  return <span className={`badge text-bg-${c}`}>{humanOrderStatus(status)}</span>;
}

function startOfDayIso(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return '';
  const t = dateStr.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return `${t}T00:00:00.000Z`;
  return t;
}

function endOfDayIso(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return '';
  const t = dateStr.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return `${t}T23:59:59.999Z`;
  return t;
}

function isSensitiveUserKey(key) {
  return /password|secret|token|hash/i.test(String(key));
}

function humanizeKey(key) {
  const spaced = String(key)
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim();
  return spaced
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function formatUserFieldValue(value, depth = 0) {
  if (value == null || value === '') return <span className="text-secondary">—</span>;
  if (typeof value === 'boolean') {
    return value ? (
      <span className="badge text-bg-success rounded-pill">Yes</span>
    ) : (
      <span className="badge text-bg-secondary rounded-pill">No</span>
    );
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return <span className="tabular-nums">{value.toLocaleString()}</span>;
  }
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value)) {
      return <span title={value}>{formatDate(value)}</span>;
    }
    return <span className="text-break">{value}</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span className="text-secondary">—</span>;
    if (depth >= 2) {
      return (
        <span className="text-secondary small">
          {value.length} item{value.length === 1 ? '' : 's'}
        </span>
      );
    }
    if (typeof value[0] !== 'object' || value[0] === null) {
      return <span>{value.map((v) => String(v)).join(', ')}</span>;
    }
    return (
      <ul className="list-unstyled mb-0 small border rounded-3 overflow-hidden">
        {value.slice(0, 6).map((item, i) => (
          <li key={i} className="px-3 py-2 border-bottom border-light bg-white">
            {formatUserFieldValue(item, depth + 1)}
          </li>
        ))}
        {value.length > 6 ? (
          <li className="px-3 py-2 text-secondary small bg-light">+{value.length - 6} more</li>
        ) : null}
      </ul>
    );
  }
  if (typeof value === 'object') {
    if (depth >= 2) {
      return (
        <span className="text-secondary small">
          {Object.keys(value).filter((k) => !isSensitiveUserKey(k)).length} fields
        </span>
      );
    }
    const entries = Object.entries(value).filter(([sk]) => !isSensitiveUserKey(sk));
    if (entries.length === 0) return <span className="text-secondary">—</span>;
    return (
      <dl className="row small mb-0 gx-2">
        {entries.map(([sk, sv]) => (
          <React.Fragment key={sk}>
            <dt className="col-sm-4 text-secondary mb-1">{humanizeKey(sk)}</dt>
            <dd className="col-sm-8 mb-2 text-break">{formatUserFieldValue(sv, depth + 1)}</dd>
          </React.Fragment>
        ))}
      </dl>
    );
  }
  return <span className="text-break">{String(value)}</span>;
}

function computeInitials(displayName, emailFallback) {
  const s = String(displayName || emailFallback || '?').trim();
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  if (parts.length === 1 && parts[0].length >= 2) return parts[0].slice(0, 2).toUpperCase();
  return s.slice(0, 1).toUpperCase() || '?';
}

function markUsed(used, key, user) {
  if (user && Object.prototype.hasOwnProperty.call(user, key) && user[key] != null && user[key] !== '') {
    used.add(key);
  }
}

/** Keys that represent the user's profile photo — shown in hero, hidden from "Additional details". */
const PROFILE_PICTURE_KEYS = [
  'profileImageUrl',
  'profilePicture',
  'profilePictureUrl',
  'profilePhoto',
  'profileImage',
  'avatar',
  'avatarUrl',
  'photo',
  'photoUrl',
  'picture',
  'imageUrl',
  'thumbUrl',
  'thumbnailUrl',
];

function pickProfilePictureUrl(user) {
  for (const key of PROFILE_PICTURE_KEYS) {
    const raw = user[key];
    if (typeof raw === 'string') {
      const t = raw.trim();
      if (t) return { url: t, key };
    }
    if (raw && typeof raw === 'object' && typeof raw.url === 'string') {
      const t = raw.url.trim();
      if (t) return { url: t, key };
    }
  }
  return null;
}

function markProfilePictureKeysUsed(used, user) {
  PROFILE_PICTURE_KEYS.forEach((k) => {
    const raw = user[k];
    if (raw == null || raw === '') return;
    if (typeof raw === 'string' || (typeof raw === 'object' && raw.url)) used.add(k);
  });
}

function isProfilePictureKey(key) {
  return PROFILE_PICTURE_KEYS.includes(key);
}

function buildProfileLayout(user, routeUserId) {
  const used = new Set();
  const resolvedId = String(user.userId ?? user.id ?? user._id ?? routeUserId ?? '');
  ['userId', 'id', '_id'].forEach((k) => markUsed(used, k, user));

  const emailAsName = formatEmailField(user.email);
  const displayName =
    user.name || user.fullName || (emailAsName !== '—' ? emailAsName : null) || 'Customer';
  ['name', 'fullName'].forEach((k) => markUsed(used, k, user));

  const emailDisplay = formatEmailField(user.email);
  markUsed(used, 'email', user);

  const phoneRaw = user.phone ?? user.phoneNumber ?? user.mobile;
  const phoneDisplay = phoneRaw != null && phoneRaw !== '' ? String(phoneRaw) : '';
  ['phone', 'phoneNumber', 'mobile'].forEach((k) => markUsed(used, k, user));

  const joined = user.createdAt || user.created_at;
  if (joined) {
    used.add('createdAt');
    used.add('created_at');
  }
  const updated = user.updatedAt || user.updated_at;
  if (updated) {
    used.add('updatedAt');
    used.add('updated_at');
  }

  const badges = [];
  const pushBadge = (keys, condition, label, variant) => {
    if (!condition) return;
    const keyList = Array.isArray(keys) ? keys : [keys];
    keyList.forEach((k) => used.add(k));
    badges.push({ label, variant });
  };

  if (user.isActive === true || user.active === true) {
    used.add('isActive');
    used.add('active');
    badges.push({ label: 'Active', variant: 'success' });
  } else if (user.isActive === false || user.active === false) {
    used.add('isActive');
    used.add('active');
    badges.push({ label: 'Inactive', variant: 'secondary' });
  }
  if (typeof user.status === 'string' && user.status) {
    used.add('status');
    badges.push({ label: user.status, variant: 'info' });
  }
  pushBadge(['emailVerified', 'isEmailVerified'], user.emailVerified === true || user.isEmailVerified === true, 'Email verified', 'primary');
  pushBadge(['phoneVerified', 'isPhoneVerified'], user.phoneVerified === true || user.isPhoneVerified === true, 'Phone verified', 'primary');

  const roleLabel = user.role || user.userRole || user.type;
  if (typeof roleLabel === 'string' && roleLabel) {
    ['role', 'userRole', 'type'].forEach((k) => used.add(k));
    badges.push({ label: roleLabel, variant: 'dark' });
  }

  const profilePicture = pickProfilePictureUrl(user);
  const profilePictureUrl = profilePicture?.url ?? null;
  markProfilePictureKeysUsed(used, user);

  const initials = computeInitials(user.name || user.fullName, userEmailPlain(user));

  const contactTiles = [];
  if (emailDisplay && emailDisplay !== '—') {
    contactTiles.push({ key: 'email', label: 'Email', value: emailDisplay });
  }
  if (phoneDisplay) {
    contactTiles.push({ key: 'phone', label: 'Mobile', value: phoneDisplay });
  }

  const timelineTiles = [];
  if (joined) timelineTiles.push({ key: 'joined', label: 'Joined', value: formatDate(joined) });
  if (updated) timelineTiles.push({ key: 'updated', label: 'Last updated', value: formatDate(updated) });

  const remainingEntries = Object.entries(user).filter(([k, v]) => {
    if (isSensitiveUserKey(k)) return false;
    if (isProfilePictureKey(k)) return false;
    if (used.has(k)) return false;
    if (v === undefined || v === null || v === '') return false;
    return true;
  });

  return {
    resolvedId,
    displayName,
    initials,
    profilePictureUrl,
    badges,
    contactTiles,
    timelineTiles,
    remainingEntries,
  };
}

function userEmailPlain(u) {
  if (!u?.email) return '';
  if (typeof u.email === 'string') return u.email;
  if (typeof u.email === 'object' && u.email !== null && typeof u.email.address === 'string') return u.email.address;
  return '';
}

export default function AppUserDetail() {
  const { userId } = useParams();

  const [data, setData] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [userError, setUserError] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');

  const [orders, setOrders] = useState([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [ordersError, setOrdersError] = useState('');
  const [orderStatus, setOrderStatus] = useState('');
  const [orderTitleContains, setOrderTitleContains] = useState('');
  const [orderDateFrom, setOrderDateFrom] = useState('');
  const [orderDateTo, setOrderDateTo] = useState('');

  const [reviews, setReviews] = useState([]);
  const [reviewsTotal, setReviewsTotal] = useState(0);
  const [reviewsSkip, setReviewsSkip] = useState(0);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [reviewsError, setReviewsError] = useState('');

  const [feedbackRows, setFeedbackRows] = useState([]);
  const [feedbackTotal, setFeedbackTotal] = useState(0);
  const [feedbackSkip, setFeedbackSkip] = useState(0);
  const [feedbackLoading, setFeedbackLoading] = useState(true);
  const [feedbackError, setFeedbackError] = useState('');

  const [avatarFailed, setAvatarFailed] = useState(false);

  const user = useMemo(() => pickUser(data), [data]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setUserLoading(true);
      setUserError('');
      try {
        const res = await adminApi.getAppUser(userId);
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) setUserError(pickErrorMessage(e));
      } finally {
        if (!cancelled) setUserLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const loadOrders = useCallback(async () => {
    setOrdersLoading(true);
    setOrdersError('');
    try {
      const params = {
        skip: 0,
        limit: ORDERS_HISTORY_LIMIT,
        userId,
      };
      if (orderStatus) params.status = orderStatus;
      const title = orderTitleContains.trim();
      if (title) {
        params.itemTitleContains = title;
        params.titleContains = title;
      }
      const from = startOfDayIso(orderDateFrom);
      const to = endOfDayIso(orderDateTo);
      if (from) params.createdFrom = from;
      if (to) params.createdTo = to;
      const raw = await adminApi.listOrders(params);
      const norm = normalizeListResponse(raw, ORDERS_HISTORY_LIMIT);
      setOrders(norm.items);
      setOrdersTotal(norm.totalCount);
    } catch (e) {
      setOrdersError(pickErrorMessage(e));
    } finally {
      setOrdersLoading(false);
    }
  }, [userId, orderStatus, orderTitleContains, orderDateFrom, orderDateTo]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const loadReviews = useCallback(async () => {
    setReviewsLoading(true);
    setReviewsError('');
    try {
      const params = { skip: reviewsSkip, limit: REVIEWS_PAGE_SIZE, userId };
      const raw = await adminApi.listReviews(params);
      const norm = normalizeListResponse(raw, REVIEWS_PAGE_SIZE);
      setReviews(norm.items);
      setReviewsTotal(norm.totalCount);
    } catch (e) {
      setReviewsError(pickErrorMessage(e));
    } finally {
      setReviewsLoading(false);
    }
  }, [userId, reviewsSkip]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const loadFeedback = useCallback(async () => {
    setFeedbackLoading(true);
    setFeedbackError('');
    try {
      const params = { skip: feedbackSkip, limit: FEEDBACK_PAGE_SIZE, userId };
      const raw = await adminApi.listFeedback(params);
      const norm = normalizeListResponse(raw, FEEDBACK_PAGE_SIZE);
      setFeedbackRows(norm.items);
      setFeedbackTotal(norm.totalCount);
    } catch (e) {
      setFeedbackError(pickErrorMessage(e));
    } finally {
      setFeedbackLoading(false);
    }
  }, [userId, feedbackSkip]);

  useEffect(() => {
    loadFeedback();
  }, [loadFeedback]);

  function openEditModal() {
    setEditError('');
    setEditName((user?.name || user?.fullName || '').trim());
    setEditEmail(userEmailPlain(user));
    setEditOpen(true);
  }

  async function submitEdit(e) {
    e.preventDefault();
    setEditError('');
    const name = editName.trim();
    const email = editEmail.trim();
    if (!name) {
      setEditError('Name is required.');
      return;
    }
    if (!email) {
      setEditError('Email is required.');
      return;
    }
    setEditSaving(true);
    try {
      const res = await adminApi.updateAppUser(userId, { name, email });
      setData(res);
      setEditOpen(false);
    } catch (err) {
      setEditError(pickErrorMessage(err));
    } finally {
      setEditSaving(false);
    }
  }

  const profile = useMemo(() => (user ? buildProfileLayout(user, userId) : null), [user, userId]);

  useEffect(() => {
    setAvatarFailed(false);
  }, [profile?.profilePictureUrl, userId]);

  const titleName = profile?.displayName || userId;
  const phoneDisplay = user?.phone || user?.phoneNumber || user?.mobile || '—';

  const orderColumns = [
    {
      key: 'orderNumber',
      label: 'Order',
      render: (r) => r.orderNumber || r.orderId || r.id || '—',
    },
    {
      key: 'status',
      label: 'Status',
      render: (r) => orderStatusBadge(r.status),
    },
    {
      key: 'veg',
      label: 'Veg',
      render: (r) => {
        const v = r.isVegOrder ?? r.vegOnly ?? r.veg ?? r.allVeg;
        if (v === true) return <span className="text-success">Yes</span>;
        if (v === false) return <span className="text-secondary">No</span>;
        return '—';
      },
    },
    {
      key: 'total',
      label: 'Total',
      render: (r) => formatMoney(r.totalAmount ?? r.total ?? r.grandTotal),
    },
    {
      key: 'created',
      label: 'Placed',
      render: (r) => {
        const iso = r.createdAt || r.placedAt || r.created_at;
        return (
          <div className="small">
            <div className="text-dark text-nowrap">{formatPlacedDate(iso)}</div>
            <div className="text-secondary order-placed-time text-nowrap">{formatPlacedTime(iso)}</div>
          </div>
        );
      },
    },
    {
      key: 'actions',
      label: '',
      width: '100px',
      render: (r) => {
        const id = r.orderId || r.id || r._id;
        return id ? (
          <Link to={`/orders/${id}`} className="btn btn-sm btn-outline-primary">
            View
          </Link>
        ) : (
          '—'
        );
      },
    },
  ];

  const reviewColumns = [
    {
      key: 'rating',
      label: 'Rating',
      render: (r) => (r.rating != null ? `${r.rating} ★` : '—'),
    },
    {
      key: 'product',
      label: 'Product',
      render: (r) =>
        r.menuItemTitle || r.itemTitle || r.productTitle || r.menuItemName || r.menuItemId || '—',
    },
    {
      key: 'comment',
      label: 'Review',
      render: (r) => (
        <span className="text-truncate d-inline-block" style={{ maxWidth: 280 }} title={r.comment || r.text}>
          {r.comment || r.text || '—'}
        </span>
      ),
    },
    {
      key: 'created',
      label: 'When',
      render: (r) => formatDate(r.createdAt || r.created_at),
    },
  ];

  if (userLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" />
      </div>
    );
  }

  return (
    <>
      <BackLink to="/app-users">App users</BackLink>
      <PageHeader
        title={titleName}
        subtitle={profile?.resolvedId ? `Account · ${profile.resolvedId}` : `User ${userId}`}
        actions={
          user ? (
            <button type="button" className="btn btn-primary" onClick={openEditModal}>
              Edit name &amp; email
            </button>
          ) : null
        }
      />
      <ErrorBanner message={userError} />

      {user ? (
        <>
          <Card className="mb-4 border-0 shadow-sm overflow-hidden">
            <div className="bg-primary bg-opacity-10 border-bottom border-primary border-opacity-25 px-4 py-4">
              <div className="d-flex flex-wrap align-items-center gap-4">
                <div
                  className="rounded-circle bg-white shadow-sm text-primary fw-bold d-flex align-items-center justify-content-center flex-shrink-0 border border-primary border-opacity-25 overflow-hidden"
                  style={{ width: '4.25rem', height: '4.25rem' }}
                >
                  {profile.profilePictureUrl && !avatarFailed ? (
                    <img
                      src={profile.profilePictureUrl}
                      alt={profile.displayName}
                      className="w-100 h-100"
                      style={{ objectFit: 'cover' }}
                      onError={() => setAvatarFailed(true)}
                    />
                  ) : (
                    <span className="d-flex align-items-center justify-content-center w-100 h-100" style={{ fontSize: '1.35rem' }} aria-hidden="true">
                      {profile.initials}
                    </span>
                  )}
                </div>
                <div className="flex-grow-1 min-w-0">
                  <h2 className="h4 mb-2 text-dark text-break">{profile.displayName}</h2>
                  <div className="text-secondary small">
                    <span
                      className="text-uppercase fw-semibold"
                      style={{ fontSize: '0.68rem', letterSpacing: '0.08em' }}
                    >
                      User ID
                    </span>
                    <div className="font-monospace text-dark user-select-all text-break mt-1">{profile.resolvedId}</div>
                  </div>
                  {profile.badges.length > 0 ? (
                    <div className="d-flex flex-wrap gap-2 mt-3">
                      {profile.badges.map((b, i) => (
                        <span key={`${b.label}-${i}`} className={`badge text-bg-${b.variant} rounded-pill`}>
                          {b.label}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
            <CardBody className="p-4">
              <h3 className="h6 text-secondary mb-3">Contact &amp; activity</h3>
              <div className="row g-3">
                {profile.contactTiles.concat(profile.timelineTiles).map((t) => (
                  <div key={t.key} className="col-sm-6 col-xl-4">
                    <div className="h-100 rounded-3 border p-3 bg-light bg-opacity-50">
                      <div
                        className="text-secondary small fw-semibold mb-1"
                        style={{ fontSize: '0.72rem', letterSpacing: '0.06em' }}
                      >
                        {t.label}
                      </div>
                      <div className="fw-medium text-break mb-0">{t.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>

          {profile.remainingEntries.length > 0 ? (
            <Card className="mb-4 border-0 shadow-sm">
              <CardBody className="p-4">
                <h3 className="h6 text-dark mb-3">Additional details</h3>
                <div className="row g-3">
                  {profile.remainingEntries.map(([key, value]) => (
                    <div key={key} className="col-md-6 col-xl-4">
                      <div className="rounded-3 border p-3 h-100 bg-white">
                        <div className="text-secondary small fw-semibold mb-2">{humanizeKey(key)}</div>
                        <div className="small text-break">{formatUserFieldValue(value)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          ) : null}

          <div className="mb-2">
            <h2 className="h5 mb-1">Order history</h2>
            <p className="text-secondary small mb-0">
              This customer&apos;s orders only — same <code className="user-select-all">listOrders</code> API with{' '}
              <code className="user-select-all">userId</code> set. No paging; up to {ORDERS_HISTORY_LIMIT} rows. For
              search across everyone, use the sidebar <Link to="/order-history">Order history</Link> page.
            </p>
          </div>
          <ErrorBanner message={ordersError} />
          <Card className="mb-3">
            <CardBody>
              <div className="row g-3 align-items-end">
                <div className="col-md-3 col-lg-2">
                  <label className="form-label small mb-1">Status</label>
                  <select
                    className="form-select"
                    value={orderStatus}
                    onChange={(e) => {
                      setOrderStatus(e.target.value);
                    }}
                  >
                    <option value="">All</option>
                    {ORDER_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {humanOrderStatus(s)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-4 col-lg-3">
                  <label className="form-label small mb-1">Title contains</label>
                  <input
                    className="form-control"
                    value={orderTitleContains}
                    onChange={(e) => setOrderTitleContains(e.target.value)}
                    placeholder="Menu item title"
                  />
                </div>
                <div className="col-md-3 col-lg-2">
                  <label className="form-label small mb-1">From date</label>
                  <input
                    className="form-control"
                    type="date"
                    value={orderDateFrom}
                    onChange={(e) => setOrderDateFrom(e.target.value)}
                  />
                </div>
                <div className="col-md-3 col-lg-2">
                  <label className="form-label small mb-1">To date</label>
                  <input
                    className="form-control"
                    type="date"
                    value={orderDateTo}
                    onChange={(e) => setOrderDateTo(e.target.value)}
                  />
                </div>
                <div className="col-md-4 col-lg-2">
                  <label className="form-label small mb-1 text-transparent user-select-none" aria-hidden="true">
                    Apply
                  </label>
                  <FilterSubmitButton onClick={() => loadOrders()} />
                </div>
              </div>
            </CardBody>
          </Card>
          <Card className="mb-4 border-0 shadow-sm">
            <CardBody className="p-0">
              {ordersLoading ? (
                <div className="d-flex justify-content-center py-5">
                  <div className="spinner-border text-primary" />
                </div>
              ) : (
                <>
                  {ordersTotal > orders.length ? (
                    <div className="alert alert-light border-bottom rounded-0 small mb-0 py-2 px-3" role="status">
                      Showing <strong>{orders.length}</strong> of <strong>{ordersTotal}</strong> matching orders (cap{' '}
                      {ORDERS_HISTORY_LIMIT}). Narrow filters or open the main <Link to="/orders">Orders</Link> list for
                      pagination.
                    </div>
                  ) : null}
                  <div className="admin-orders-scroll-boundary">
                    <DataTable columns={orderColumns} rows={orders} rowKey={(r, i) => r.orderId || r.id || r._id || i} />
                  </div>
                </>
              )}
            </CardBody>
          </Card>

          <h2 className="h5 mb-3">Product ratings &amp; reviews</h2>
          <ErrorBanner message={reviewsError} />
          <Card className="mb-4">
            <CardBody className="p-0">
              {reviewsLoading ? (
                <div className="d-flex justify-content-center py-5">
                  <div className="spinner-border text-primary" />
                </div>
              ) : (
                <DataTable columns={reviewColumns} rows={reviews} rowKey={(r, i) => r.reviewId || r.id || r._id || i} />
              )}
              <Pagination
                totalCount={reviewsTotal}
                skip={reviewsSkip}
                limit={REVIEWS_PAGE_SIZE}
                onChange={({ skip: s }) => setReviewsSkip(s)}
              />
            </CardBody>
          </Card>

          <h2 className="h5 mb-3">Feedback</h2>
          <ErrorBanner message={feedbackError} />
          <Card className="mb-4">
            <CardBody>
              {feedbackLoading ? (
                <div className="d-flex justify-content-center py-4">
                  <div className="spinner-border text-primary" />
                </div>
              ) : feedbackRows.length === 0 ? (
                <p className="text-secondary mb-0">No feedback from this user.</p>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {feedbackRows.map((row, i) => {
                    const id = row.feedbackId || row.id || row._id || i;
                    return (
                      <div key={id} className="border rounded p-3 bg-light">
                        <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
                          <span className="fw-medium">Rating: {row.rating ?? '—'}</span>
                          <span className="small text-secondary">{formatDate(row.createdAt || row.created_at)}</span>
                        </div>
                        <p className="mb-0 text-break">{row.message || row.comment || '—'}</p>
                      </div>
                    );
                  })}
                </div>
              )}
              {!feedbackLoading && feedbackRows.length > 0 ? (
                <div className="mt-3">
                  <Pagination
                    totalCount={feedbackTotal}
                    skip={feedbackSkip}
                    limit={FEEDBACK_PAGE_SIZE}
                    onChange={({ skip: s }) => setFeedbackSkip(s)}
                  />
                </div>
              ) : null}
            </CardBody>
          </Card>
        </>
      ) : (
        <Card>
          <CardBody>
            <p className="text-secondary mb-0">User not found or empty response.</p>
          </CardBody>
        </Card>
      )}

      {editOpen ? (
        <Modal
          title="Edit user"
          onClose={() => {
            if (!editSaving) setEditOpen(false);
          }}
          footer={
            <>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setEditOpen(false)} disabled={editSaving}>
                Cancel
              </button>
              <button type="submit" form="app-user-edit-form" className="btn btn-primary" disabled={editSaving}>
                {editSaving ? 'Saving…' : 'Save'}
              </button>
            </>
          }
        >
          <form id="app-user-edit-form" onSubmit={submitEdit}>
            <ErrorBanner message={editError} />
            <div className="mb-3">
              <label className="form-label" htmlFor="edit-user-name">
                Name
              </label>
              <input
                id="edit-user-name"
                className="form-control"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                autoComplete="name"
              />
            </div>
            <div className="mb-3">
              <label className="form-label" htmlFor="edit-user-email">
                Email
              </label>
              <input
                id="edit-user-email"
                type="email"
                className="form-control"
                value={editEmail}
                onChange={(e) => setEditEmail(e.target.value)}
                autoComplete="email"
              />
            </div>
            <div className="mb-0">
              <label className="form-label text-secondary">Mobile (read-only)</label>
              <input className="form-control bg-light" value={phoneDisplay === '—' ? '' : phoneDisplay} readOnly tabIndex={-1} />
            </div>
          </form>
        </Modal>
      ) : null}
    </>
  );
}
